import classNames from 'classnames';
import { ChangeEvent, FocusEvent, forwardRef, useCallback, useMemo, useRef } from 'react';
import styles from './CurrencyInput.module.scss';
import * as T from './CurrencyInput.types';
import { Mask } from './Mask';

const CurrencyInput = forwardRef<HTMLInputElement, T.CurrencyInputProps>(
  (
    {
      className,
      value,
      initialValue,
      selectOnFocus = false,
      precision = 0,
      thousandSeparator = '.',
      decimalSeparator = ',',
      allowNegative = true,
      allowNull = false,
      skin = 'gray',
      onChange,
      onChangeValue,
      onFocus,
      onChangeEvent,
      onBlur,
      ...inputProps
    },
    ref,
  ) => {
    const { allowEmpty = false, ...props } = inputProps as T.LegacyCurrencyInputProps;
    const isNullable = allowNull === true;
    const legacyValue = value === undefined ? 0 : value;
    const currentValue = isNullable ? value ?? null : legacyValue ?? 0;
    const callbackValue = isNullable ? currentValue : legacyValue;
    const valueToMask = isNullable ? (initialValue !== undefined ? initialValue : currentValue) : initialValue || currentValue;
    const focusRef = useRef<number | null>(callbackValue);

    const currency = useMemo(() => {
      return {
        precision,
        thousandSeparator,
        decimalSeparator,
        allowEmpty: isNullable ? false : allowEmpty,
        allowNegative,
      };
    }, [precision, thousandSeparator, decimalSeparator, allowEmpty, allowNegative, isNullable]);

    const mask = useMemo(() => {
      if (isNullable && valueToMask === null) return '';
      return new Mask(valueToMask, currency).mask;
    }, [valueToMask, currency, isNullable]);

    const emitChangeValue = onChangeValue as unknown as ((value: number | null) => void) | undefined;
    const emitChangeEvent = onChangeEvent as unknown as ((e: ChangeEvent<HTMLInputElement>, mask: string, float: number | null) => void) | undefined;
    const emitBlur = onBlur as unknown as ((e: FocusEvent<HTMLInputElement>, details: T.BlurDetails<number | null>) => void) | undefined;

    const handleChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;

        try {
          const inputType = (event.nativeEvent as InputEvent).inputType;
          const isPartialZeroDeletion =
            isNullable &&
            currentValue === 0 &&
            value !== '' &&
            value.length < mask.length &&
            new Mask(value, currency).float === 0;
          const isDeletingFormattedZero = isPartialZeroDeletion && Boolean(inputType?.startsWith('delete'));
          const next = isNullable && (value === '' || isDeletingFormattedZero) ? null : new Mask(value, currency);
          onChange?.(event);
          emitChangeEvent?.(event, next === null ? '' : next.mask, next === null ? null : next.float);
          emitChangeValue?.(next === null ? null : next.float);
        } catch (error) {
          console.warn('Error processing currency input:', error);
          onChange?.(event);
          emitChangeEvent?.(event, value, isNullable ? null : 0);
          emitChangeValue?.(isNullable ? null : 0);
        }
      },
      [onChange, emitChangeValue, emitChangeEvent, currency, currentValue, isNullable, mask],
    );

    const handleFocus = useCallback(
      (event: FocusEvent<HTMLInputElement>) => {
        onFocus?.(event);
        focusRef.current = callbackValue;
        if (selectOnFocus) event.target.select();
      },
      [onFocus, callbackValue, selectOnFocus],
    );

    const handleBlur = useCallback(
      (e: FocusEvent<HTMLInputElement>) => {
        const details = {
          onFocusValue: focusRef.current,
          onBlurValue: callbackValue,
          hasChanges: focusRef.current !== callbackValue,
        };
        emitBlur?.(e, details);
      },
      [focusRef, callbackValue, emitBlur],
    );

    return (
      <input
        {...props}
        ref={ref}
        className={classNames(styles.input, className)}
        type="text"
        value={mask}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        data-skin={skin}
      />
    );
  },
);

CurrencyInput.displayName = 'CurrencyInput';

export { CurrencyInput };
